import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { DatePicker, Drawer, Dropdown, Menu } from 'antd'
import { useHistory } from 'react-router-dom'
import Select from 'react-select'
import { BiSelectMultiple } from 'react-icons/bi'
import { AiOutlineDown } from 'react-icons/ai'

import Receive from '../../components/reusable/receive'
import ClaimStatusCard from '../../components/reusable/claimStatusCard'
import Modal from '../../components/reusable/modal'
import Confirm from '../../components/reusable/confirm'
import RecordShaRemittance from '../../components/drawers/recordShaRemittance'
import { stringifyDate, useListData, usePet, useQuery, useToggle } from '../../modules'

const { RangePicker } = DatePicker

const serviceTypeOptions = [
    { label: 'All service types', value: 'all' },
    { label: 'Outpatient', value: 'OUTPATIENT' },
    { label: 'Inpatient', value: 'INPATIENT' },
    { label: 'Capitation', value: 'CAPITATION' },
    { label: 'Emergency', value: 'EMERGENCY' },
]

const statusOptions = [
    { label: 'All statuses', value: 'all' },
    { label: 'Draft', value: 'draft' },
    { label: 'Submitted', value: 'submitted' },
    { label: 'Accepted', value: 'accepted' },
    { label: 'Rejected', value: 'rejected' },
]

const modeOptions = [
    { label: 'Ambulance', value: 'AMBULANCE' },
    { label: 'Walk-in', value: 'WALK-IN' },
    { label: 'Other', value: 'OTHER' },
]

const broughtByOptions = [
    { label: 'Relative', value: 'RELATIVE' },
    { label: 'Unknown', value: 'UNKNOWN' },
    { label: 'Samaritan', value: 'SAMARITAN' },
    { label: 'Paramedics', value: 'PARAMEDICS' },
]

const identificationTypeOptions = [
    { label: 'National ID', value: 'National ID' },
    { label: 'Passport', value: 'Passport' },
    { label: 'Registration Number', value: 'registration_number' },
]

const claimOutstanding = claim => (claim?.financials?.totalAmount ?? 0) - (claim?.paidAmount ?? 0)

const ShaClaims = () => {
    const history = useHistory()
    const query = useQuery()
    const initialClaim = query.get('claim') || undefined
    const [string, setString] = useState('')
    const [ranges, setRanges] = useState([moment().subtract(30, 'days').startOf('day'), moment().endOf('day')])
    const [selectedServiceType, setSelectedServiceType] = useState(serviceTypeOptions[0])
    const [selectedStatus, setSelectedStatus] = useState(statusOptions[0])
    const [selectedClaim, setSelectedClaim] = useState(null)
    const [detailsVisible, setDetailsVisible] = useState(Boolean(initialClaim))
    const [createVisible, setCreateVisible] = useState(false)
    const [patientString, setPatientString] = useState('')
    const [selectedPatient, setSelectedPatient] = useState(null)
    const [otp, setOtp] = useState('')
    const [interventionCode, setInterventionCode] = useState('')
    const [referenceNumber, setReferenceNumber] = useState('')
    const [identificationNumber, setIdentificationNumber] = useState('')
    const [regulationBody, setRegulationBody] = useState('')
    const [notes, setNotes] = useState('')
    const [modeOfArrival, setModeOfArrival] = useState(modeOptions[2])
    const [broughtBy, setBroughtBy] = useState(broughtByOptions[1])
    const [identificationType, setIdentificationType] = useState(identificationTypeOptions[0])

    const [chosen, setChosen] = useState([])
    const [select, toggleSelect] = useToggle()
    const [remittanceVisible, toggleRemittanceVisible] = useToggle()

    const [
        { loading, error, fetch },
        { list: claims = [], count = 0 },
        { page = 1, limit = 10, navigate, Paginate },
    ] = useListData('shaClaims', {
        string,
        status: selectedStatus?.value,
        serviceType: selectedServiceType?.value,
        from: ranges?.[0]?._d ?? ranges?.[0],
        to: ranges?.[1]?._d ?? ranges?.[1],
    }, { notify: true, manual: true }, 'claims')

    const [
        { loading: patientsLoading },
        { list: patients = [] },
    ] = useListData('patients', { string: patientString }, { notify: false }, 'patients', 20)

    const [, fetchFocusedClaim] = usePet('claimStatus', {
        id: initialClaim,
    }, {
        notify: false,
        manual: true,
        callback: ({ data }) => {
            if (!data?._id) return
            setSelectedClaim(data)
            setDetailsVisible(true)
        }
    })

    const [{ loading: creatingEmergency }, createEmergencyClaim] = usePet('createEmergencyClaim', {
        patient: selectedPatient?.value,
        otp,
        interventionCode,
        modeOfArrival: modeOfArrival?.value,
        broughtBy: broughtBy?.value,
        referenceNumber,
        identificationNumber,
        identificationType: identificationType?.value,
        regulationBody,
        notes,
    }, {
        notify: true,
        manual: true,
        load: { msg: 'Creating emergency SHA claim...' },
        callback: ({ data }) => {
            setCreateVisible(false)
            setSelectedClaim(data?.claim)
            setDetailsVisible(true)
            setSelectedServiceType(serviceTypeOptions.find(option => option.value === 'EMERGENCY') ?? serviceTypeOptions[0])
            setOtp('')
            setInterventionCode('')
            setReferenceNumber('')
            setIdentificationNumber('')
            setRegulationBody('')
            setNotes('')
            setSelectedPatient(null)
            fetch()
        }
    })

    useEffect(() => {
        fetch()
    }, [page]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        navigate(1)
        fetch()
    }, [string, selectedStatus?.value, selectedServiceType?.value, ranges?.[0]?.valueOf?.() ?? ranges?.[0], ranges?.[1]?.valueOf?.() ?? ranges?.[1]]) // eslint-disable-line react-hooks/exhaustive-deps

    const patientOptions = useMemo(() =>
        patients.map(patient => ({
            value: patient?._id,
            label: `${patient?.name}${patient?.beneficiaryCrId ? ` • ${patient.beneficiaryCrId}` : ''}${patient?.identification ? ` • ${patient.identification}` : ''}`,
            ...patient,
        })),
    [patients])

    const openClaim = claim => {
        setSelectedClaim(claim)
        setDetailsVisible(true)
        history.replace(`/app/accounts/sales/sha-claims?claim=${claim?._id}`)
    }

    const requiresOtp = Boolean(selectedPatient?.beneficiaryCrId)

    const choose = id => {
        let temp = [...chosen]

        if (temp.includes(id)) {
            temp = temp.filter(x => x !== id)
        } else {
            temp.push(id)
        }

        setChosen(temp)
    }

    const togSelect = () => {
        setChosen([])
        toggleSelect()
    }

    const chosenClaims = claims.filter(claim => chosen.includes(claim._id))
    const chosenInsurerIds = Array.from(new Set(chosenClaims.map(claim => claim?.insurer)))
    const chosenInsurer = chosenInsurerIds.length === 1 ? chosenClaims[0]?.insurerData : null
    const chosenPayableClaims = chosenClaims.filter(claim => claimOutstanding(claim) > 0)

    const openRemittance = () => {
        if (chosenInsurerIds.length !== 1) {
            Confirm({
                title: 'Cannot record remittance',
                message: 'All selected claims must belong to the same insurer.',
                okText: 'OK',
            })
            return
        }

        if (chosenPayableClaims.length !== chosenClaims.length) {
            Confirm({
                title: 'Cannot record remittance',
                message: 'Remove any fully remitted claims from the selection before recording a new remittance.',
                okText: 'OK',
            })
            return
        }

        toggleRemittanceVisible()
    }

    const onRemittancePerform = () => {
        togSelect()
        fetch()
    }

    useEffect(() => {
        if (!initialClaim) return

        const matched = claims.find(claim => claim?._id === initialClaim)
        if (matched) {
            setSelectedClaim(matched)
            setDetailsVisible(true)
            return
        }

        fetchFocusedClaim()
    }, [initialClaim, claims]) // eslint-disable-line react-hooks/exhaustive-deps

    const closeDetails = () => {
        setDetailsVisible(false)
        setSelectedClaim(null)
        history.replace('/app/accounts/sales/sha-claims')
    }

    return (
        <div className="px-4 sm:px-6 lg:px-8 py-8 w-full mx-auto">
            {
                createVisible &&
                <Modal
                    visible={ createVisible }
                    toggle={ () => setCreateVisible(false) }
                    title="Create Emergency SHA Claim"
                    w={ 760 }>
                    <div className="space-y-4">
                        <div className="rounded-sm border border-yellow-200 bg-yellow-50 px-4 py-3">
                            <p className="text-sm text-yellow-900 mb-1">Use this only for emergency scenarios that need the 24-hour SHA emergency submission workflow.</p>
                            <p className="text-xs text-yellow-800 mb-0">If you link a patient who already has a beneficiary CR ID, capture their OTP here at creation time.</p>
                        </div>

                        <div>
                            <label className="block text-xs text-gray-500 mb-1">Patient (optional)</label>
                            <Select
                                value={ selectedPatient }
                                onInputChange={ setPatientString }
                                onChange={ setSelectedPatient }
                                isLoading={ patientsLoading }
                                options={ patientOptions }
                                placeholder="Search patient to create an identified emergency claim"
                            />
                        </div>

                        {
                            requiresOtp &&
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Patient OTP</label>
                                <input
                                    value={ otp }
                                    onChange={ e => setOtp(e.target.value) }
                                    placeholder="OTP for identified emergency patient"
                                    className="appearance-none block w-full bg-gray-100 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                                />
                            </div>
                        }

                        <div className="grid gap-4 md:grid-cols-2">
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Intervention Code</label>
                                <input
                                    value={ interventionCode }
                                    onChange={ e => setInterventionCode(e.target.value) }
                                    placeholder="SHA intervention code"
                                    className="appearance-none block w-full bg-gray-100 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Reference Number</label>
                                <input
                                    value={ referenceNumber }
                                    onChange={ e => setReferenceNumber(e.target.value) }
                                    placeholder="Facility emergency reference"
                                    className="appearance-none block w-full bg-gray-100 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Mode of Arrival</label>
                                <Select value={ modeOfArrival } onChange={ setModeOfArrival } options={ modeOptions } />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Brought By</label>
                                <Select value={ broughtBy } onChange={ setBroughtBy } options={ broughtByOptions } />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Practitioner ID Number</label>
                                <input
                                    value={ identificationNumber }
                                    onChange={ e => setIdentificationNumber(e.target.value) }
                                    placeholder="Doctor / practitioner ID"
                                    className="appearance-none block w-full bg-gray-100 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                                />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-500 mb-1">Practitioner ID Type</label>
                                <Select value={ identificationType } onChange={ setIdentificationType } options={ identificationTypeOptions } />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-xs text-gray-500 mb-1">Regulation Body (optional)</label>
                                <input
                                    value={ regulationBody }
                                    onChange={ e => setRegulationBody(e.target.value) }
                                    placeholder="Medical regulator"
                                    className="appearance-none block w-full bg-gray-100 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                                />
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-xs text-gray-500 mb-1">Notes (optional)</label>
                                <textarea
                                    rows={ 3 }
                                    value={ notes }
                                    onChange={ e => setNotes(e.target.value) }
                                    placeholder="Emergency notes"
                                    className="appearance-none block w-full bg-gray-100 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                                />
                            </div>
                        </div>

                        <div className="flex justify-end gap-2">
                            <button onClick={ () => setCreateVisible(false) } className="btn bg-white border border-gray-300 hover:bg-gray-50">Cancel</button>
                            <button
                                disabled={ creatingEmergency || !interventionCode.trim() || !referenceNumber.trim() || !identificationNumber.trim() || (requiresOtp && !otp.trim()) }
                                onClick={ createEmergencyClaim }
                                className="btn hisani-btn-orange text-white">
                                Create Emergency Claim
                            </button>
                        </div>
                    </div>
                </Modal>
            }

            {
                detailsVisible &&
                <Drawer
                    visible={ detailsVisible }
                    width="46%"
                    onClose={ closeDetails }
                    closable
                    maskClosable
                    title={ selectedClaim?.patientData?.name ?? selectedClaim?.externalClaimId ?? 'SHA claim' }>
                    {
                        selectedClaim &&
                        <ClaimStatusCard claimId={ selectedClaim?._id } admissionId={ selectedClaim?.admission } orderId={ selectedClaim?.order } />
                    }
                </Drawer>
            }

            {
                remittanceVisible &&
                <RecordShaRemittance
                    visible={ remittanceVisible }
                    claims={ chosenPayableClaims }
                    insurer={ chosenInsurer }
                    onClose={ toggleRemittanceVisible }
                    onPerform={ onRemittancePerform }
                />
            }

            <div className="sm:flex sm:justify-between sm:items-start mb-6 gap-4">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800 mb-1">SHA Claims Workbench</h1>
                    <p className="text-sm text-gray-500 mb-0">Create emergency claims, monitor draft/submitted claims, and continue SHA work from one place.</p>
                </div>
                <div className="flex items-center">
                    <button
                        onClick={ () => setCreateVisible(true) }
                        className="btn hisani-btn-orange text-white">
                        New Emergency Claim
                    </button>
                    {
                        chosen.length > 0 ?
                            <Dropdown
                                overlay={
                                    <Menu>
                                        <Menu.Item onClick={ togSelect }>Deselect All</Menu.Item>
                                        <Menu.Item onClick={ openRemittance }>Record Remittance</Menu.Item>
                                    </Menu>
                                }>
                                <button className="btn hisani-btn-green-light ml-2 text-white">
                                    <span className="hidden xs:block mr-2">Bulk Actions ({chosen.length})</span>
                                    <AiOutlineDown />
                                </button>
                            </Dropdown>
                            :
                            <button onClick={ togSelect } className="btn ml-2 hisani-btn-green-light text-white">
                                <BiSelectMultiple style={{ color: "white" }} />
                                <span className="hidden xs:block ml-2">{!select ? "Select" : "Deselect All"}</span>
                            </button>
                    }
                </div>
            </div>

            <div className="bg-white shadow-lg rounded-sm border border-gray-200 p-4 mb-4">
                <div className="grid gap-3 md:grid-cols-4 sm:grid-cols-1">
                    <input
                        value={ string }
                        onChange={ e => setString(e.target.value) }
                        className="appearance-none block w-full bg-gray-100 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white"
                        type="text"
                        placeholder="Search patient, claim ref, consent token"
                    />
                    <Select value={ selectedServiceType } onChange={ setSelectedServiceType } options={ serviceTypeOptions } />
                    <Select value={ selectedStatus } onChange={ setSelectedStatus } options={ statusOptions } />
                    <RangePicker value={ ranges } onChange={ value => setRanges(value ?? []) } />
                </div>
            </div>

            <Receive loading={ loading } error={ error } progress>
                {
                    claims.length > 0 ?
                        <div className="bg-white shadow-lg rounded-sm border border-gray-200 p-4">
                            <div className="flex items-center justify-between mb-3">
                                <p className="text-sm font-semibold text-gray-800 mb-0">{count} SHA claim{count === 1 ? '' : 's'}</p>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="table-auto w-full">
                                    <thead className="text-xs uppercase text-gray-400 bg-gray-50 rounded-sm">
                                        <tr>
                                            { select ? <th className="p-2 text-center">Selected</th> : null }
                                            <th className="p-2 text-left">Patient / Claim</th>
                                            <th className="p-2 text-left">Service</th>
                                            <th className="p-2 text-left">Status</th>
                                            <th className="p-2 text-left">Outstanding</th>
                                            <th className="p-2 text-left">Updated</th>
                                            <th className="p-2 text-left">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-sm font-medium divide-y divide-gray-100">
                                        {
                                            claims.map(claim =>
                                                <tr key={ claim?._id }>
                                                    {
                                                        select &&
                                                        <td className="p-2">
                                                            <div className="text-center">
                                                                {
                                                                    claim?.status === 'accepted' && claimOutstanding(claim) > 0 ?
                                                                        <input
                                                                            onChange={ () => choose(claim._id) }
                                                                            checked={ chosen.includes(claim._id) }
                                                                            type="checkbox"
                                                                            className="form-checkbox cursor-pointer h-5 w-5" />
                                                                        :
                                                                        null
                                                                }
                                                            </div>
                                                        </td>
                                                    }
                                                    <td className="p-2">
                                                        <p className="mb-1">{claim?.patientData?.name ?? 'Unknown patient'}</p>
                                                        <p className="text-xs text-gray-500 mb-0">
                                                            {claim?.externalClaimId ?? claim?._id}
                                                            {claim?.emergency?.submissionDeadline ? ` • deadline ${stringifyDate(claim.emergency.submissionDeadline)}` : ''}
                                                        </p>
                                                    </td>
                                                    <td className="p-2">{claim?.serviceType ?? '—'}</td>
                                                    <td className="p-2">{claim?.status ?? 'draft'}</td>
                                                    <td className="p-2">
                                                        <p className="mb-0">Ksh. {claimOutstanding(claim)}</p>
                                                        <p className="text-xs text-gray-500 mb-0">{claim?.remittanceStatus ?? 'unpaid'}</p>
                                                    </td>
                                                    <td className="p-2">{stringifyDate(claim?.updatedOn ?? claim?.createdOn)}</td>
                                                    <td className="p-2">
                                                        <button onClick={ () => openClaim(claim) } className="btn bg-white border border-gray-300 hover:bg-gray-50">
                                                            Open
                                                        </button>
                                                    </td>
                                                </tr>
                                            )
                                        }
                                    </tbody>
                                </table>
                            </div>
                            {Paginate}
                        </div>
                        :
                        <div className="bg-white shadow-lg rounded-sm border border-gray-200 p-8">
                            <p className="text-sm text-gray-500 mb-0">No SHA claims matched the current filters.</p>
                        </div>
                }
            </Receive>
        </div>
    )
}

export default ShaClaims
